/**
 * SecureScoreTab — editorial redesign per security-center-subpages-redesign.md §7.
 *
 * Phase A+B scope:
 * - Composer-driven standfirst (composeSecureScoreBrief) wraps the page in
 *   PillarBrief with the BPA tone.
 * - Category breakdown preserved in the existing CategoryBreakdown component
 *   (Phase C upgrade swaps it for the editorial triptych).
 * - Recommendations queue rebuilt with editorial dropcap rows.
 * - Impact-vs-cost scatter matrix (§7.3) — inline SVG, no chart library.
 * - Threats-mitigated and Coverage-by-service sections wired to the helpers
 *   shipped in src/lib/security-center.
 *
 * Hard preserves: refresh mutation, SecureScoreEmptyState, ref-exposed
 * `refresh()` method.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import { SectionEyebrow, SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { CategoryBreakdown } from '@/components/SecureScore/CategoryBreakdown'
import { SecureScoreEmptyState } from '@/components/SecureScore/SecureScoreEmptyState'
import { composeSecureScoreBrief } from '@/lib/security-center/secure-score-brief'
import { severityFromSecureScoreRank } from '@/lib/security-center/secure-score-severity'
import {
  aggregateThreats,
  severityFromCoverage,
  type CoverageSeverity,
} from '@/lib/security-center/threat-aggregator'
import { coverageByService } from '@/lib/security-center/service-coverage'
import {
  placeOnMatrix,
  isMatrixClassified,
  X_BY_COST,
  type CostImpactBucket,
  type MatrixTone,
} from '@/lib/security-center/secure-score-matrix'

import type { SecureScoreSummary, SecureScoreControlProfile } from '@/types'

const SEV_BAR: Record<CoverageSeverity | 'crit' | 'high' | 'med', string> = {
  crit: '#C84B4B',
  high: '#C9AD80',
  med: '#4F86A8',
  low: '#4A9B6E',
}

const SEV_RANK: Record<'crit' | 'high' | 'med', string> = {
  crit: '#C84B4B',
  high: '#C9AD80',
  med: '#4F86A8',
}

export interface SecureScoreTabProps {
  tenantId: string
  onSummaryChange?: (summary: SecureScoreSummary | null, isRefreshing: boolean) => void
}

export interface SecureScoreTabRef {
  refresh: () => void
}

export function formatLastUpdated(fetchedAt?: string): string {
  if (!fetchedAt) return 'Unknown'
  const date = new Date(fetchedAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const SecureScoreTab = forwardRef<SecureScoreTabRef, SecureScoreTabProps>(
  function SecureScoreTab({ tenantId, onSummaryChange }, ref) {
    const queryClient = useQueryClient()
    const { accounts } = useMsal()
    const userEmail = accounts[0]?.username
    const [isRefreshing, setIsRefreshing] = useState(false)

    const {
      data: summary,
      isLoading,
      error,
      refetch,
    } = useQuery<SecureScoreSummary | null>({
      queryKey: ['secureScoreSummary', tenantId],
      queryFn: () => api.getSecureScoreSummary(tenantId),
      enabled: !!tenantId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })

    const refreshMutation = useMutation({
      mutationFn: () => api.refreshSecureScore(tenantId),
      onMutate: () => setIsRefreshing(true),
      onSuccess: (data) => {
        if (data) {
          queryClient.setQueryData(['secureScoreSummary', tenantId], data)
          toast.success('Secure Score synced successfully')
        }
      },
      onError: () => toast.error('Failed to sync Secure Score'),
      onSettled: () => setIsRefreshing(false),
    })

    const handleRefresh = () => refreshMutation.mutate()

    useImperativeHandle(ref, () => ({ refresh: handleRefresh }), [handleRefresh])

    useEffect(() => {
      if (onSummaryChange) {
        onSummaryChange(summary ?? null, isRefreshing)
      }
    }, [summary, isRefreshing, onSummaryChange])

    /* ------------------------- Brief composition ------------------------- */
    const brief = useMemo(() => {
      const cats = summary?.categoryBreakdown
      const topRecs =
        summary?.recommendations
          ?.filter(r => !r.isImplemented)
          .map(r => ({
            title: r.title,
            gap: Math.max(0, (r.maxScore ?? 0) - (r.achievedScore ?? 0)),
            category: r.controlCategory,
          }))
          .sort((a, b) => b.gap - a.gap)
          .slice(0, 3) ?? []
      const totalPtsToGain =
        summary?.recommendations
          ?.filter(r => !r.isImplemented)
          .reduce((sum, r) => sum + Math.max(0, (r.maxScore ?? 0) - (r.achievedScore ?? 0)), 0) ?? 0

      return composeSecureScoreBrief({
        userEmail,
        tenantId,
        score: summary?.score ?? null,
        topRecs,
        categories: cats
          ? {
              identity: cats.identity?.percentage ?? 0,
              data: cats.data?.percentage ?? 0,
              apps: cats.apps?.percentage ?? 0,
              device: cats.device?.percentage ?? 0,
            }
          : undefined,
        totalPtsToGain,
        delta30d: null, // Phase C: hook into useSecureScoreHistory once available.
      })
    }, [summary, tenantId, userEmail])

    /* ------------------------- Loading / error / empty ------------------- */
    if (isLoading) return <SecureScoreLoadingSkeleton />

    if (error) {
      return (
        <div
          data-tone="bpa"
          className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-8 text-center"
        >
          <h3 className="text-lg font-semibold text-[color:var(--color-navy,#212938)]">
            Failed to load Secure Score
          </h3>
          <p className="mt-2 text-sm italic text-[color:var(--color-mute,#6B7280)]">
            {error instanceof Error ? error.message : 'An error occurred.'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-[#212938] px-5 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722]"
          >
            Retry
          </button>
        </div>
      )
    }

    if (!summary || !summary.score || summary.score.maxScore === 0) {
      return <SecureScoreEmptyState tenantId={tenantId} />
    }

    /* ------------------------- Render ------------------------------------ */
    const recs = summary.recommendations ?? []
    const openRecs = recs.filter(r => !r.isImplemented)
    const threats = aggregateThreats(recs)
    const services = coverageByService(recs)

    return (
      <div data-tone="bpa">
        <PillarBrief
          brief={brief}
          score={summary.score.percentage}
          components={[]}
          tone="bpa"
          isLoading={isRefreshing}
          scoreLabel="Microsoft Secure Score"
        />

        <SyncBadge
          createdDateTime={summary.score.createdDateTime}
          isSyncing={isRefreshing}
          onSync={handleRefresh}
        />

        {/* About this score — provenance + source link */}
        <aside className="mt-8 max-w-3xl border-l-2 border-[color:var(--color-rule,#E6E1D5)] pl-4">
          <p className="text-[13px] italic leading-relaxed text-[color:var(--color-mute,#6B7280)]">
            Microsoft Secure Score is Microsoft's own posture rating, built from controls across Identity, Data, Apps, and Device.
            Every number and recommendation on this page comes straight from Microsoft 365 Defender.
            Guardian just frames the story.
          </p>
          <a
            href="https://security.microsoft.com/securescore"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-navy,#212938)] no-underline hover:underline"
          >
            View source in Defender ↗
          </a>
        </aside>

        {/* Section 01 — Category breakdown (existing component for now) */}
        <section className="mt-10">
          <SectionEyebrow text="Section · 01 · Category breakdown" />
          <SectionHeadline level="page">
            Four <em>categories</em> tell the story.
          </SectionHeadline>
          <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
            Identity, Data, Apps, and Device. Each carries its own gaps and quick wins.
          </p>
          <div className="mt-6">
            <CategoryBreakdown breakdown={summary.categoryBreakdown} />
          </div>
        </section>

        {/* Section 02 — Recommendations queue */}
        <section className="mt-12">
          <SectionEyebrow text="Section · 02 · Top recommendations" />
          <SectionHeadline level="page">
            Where <em>the points</em> are sitting unclaimed.
          </SectionHeadline>
          <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
            Sorted by point gain, then implementation cost. Quick wins first.
          </p>
          <RecommendationsList recs={openRecs} />
        </section>

        {/* Section 03 — Impact-vs-cost matrix */}
        {openRecs.length > 0 && (
          <section className="mt-12">
            <SectionEyebrow text="Section · 03 · Impact vs cost" />
            <SectionHeadline level="page">
              Where the <em>quick wins</em> sit.
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Each open recommendation plotted by implementation cost and points to gain. Top-left is where the easy points hide.
            </p>
            <ImpactCostMatrix recs={openRecs} />
          </section>
        )}

        {/* Section 04 — Threats currently mitigated */}
        {threats.length > 0 && (
          <section className="mt-12">
            <SectionEyebrow text="Section · 04 · Threat coverage" />
            <SectionHeadline level="page">
              Threats <em>currently mitigated.</em>
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Worst coverage leads. Open a row to see the controls protecting against it — and jump to Defender to act.
            </p>
            <ThreatList threats={threats} recs={recs} />
          </section>
        )}

        {/* Section 05 — Coverage by service */}
        {services.length > 0 && (
          <section className="mt-12">
            <SectionEyebrow text="Section · 05 · Coverage by service" />
            <SectionHeadline level="page">
              Coverage <em>by service.</em>
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Per-service implemented vs total. Synced from Microsoft Secure Score.
            </p>
            <ServiceCoverage rows={services} />
            <p className="mt-4 text-[11px] italic text-[color:var(--color-faint,#9CA3AF)]">
              Synced from Microsoft Secure Score. Categorization follows Microsoft's published service taxonomy.
            </p>
          </section>
        )}
      </div>
    )
  },
)

export default SecureScoreTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function SyncBadge({
  createdDateTime,
  isSyncing,
  onSync,
}: {
  createdDateTime: string | undefined
  isSyncing: boolean
  onSync: () => void
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-4">
      {createdDateTime ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span>Last synced: {new Date(createdDateTime).toLocaleString()}</span>
        </div>
      ) : (
        <div />
      )}
      <button
        onClick={onSync}
        disabled={isSyncing}
        className="inline-flex items-center gap-2 rounded-full bg-[#212938] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowPathIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
        {isSyncing ? 'Syncing…' : 'Sync Secure Score'}
      </button>
    </div>
  )
}

function RecommendationsList({ recs }: { recs: SecureScoreControlProfile[] }) {
  if (recs.length === 0) {
    return (
      <div className="mt-6 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
        No open recommendations. The score is doing its job.
      </div>
    )
  }

  // Sort: gap desc, then cost asc, then title.
  const sorted = [...recs].sort((a, b) => {
    const gA = Math.max(0, (a.maxScore ?? 0) - (a.achievedScore ?? 0))
    const gB = Math.max(0, (b.maxScore ?? 0) - (b.achievedScore ?? 0))
    if (gA !== gB) return gB - gA
    const costRank = { Low: 0, Moderate: 1, High: 2 } as const
    const cA = costRank[a.implementationCost ?? 'Moderate']
    const cB = costRank[b.implementationCost ?? 'Moderate']
    if (cA !== cB) return cA - cB
    return (a.title ?? '').localeCompare(b.title ?? '')
  })

  return (
    <ol className="mt-6 grid grid-cols-1 gap-x-10 gap-y-1 lg:grid-cols-2">
      {sorted.slice(0, 12).map((rec, idx) => {
        const gap = Math.max(0, (rec.maxScore ?? 0) - (rec.achievedScore ?? 0))
        const sev = severityFromSecureScoreRank(rec.rank, gap)
        const { whole, frac } = splitPoints(gap)
        return (
          <li
            key={rec.id}
            className="group relative cursor-pointer border-b border-[color:var(--color-line,#E6E8EC)] transition-colors hover:bg-[color:var(--color-cream,#FBF8F2)]/40"
          >
            <a
              href={defenderLinkFor(rec)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-5 py-4 no-underline"
            >
              <span
                aria-hidden
                className="font-display flex-none select-none italic font-extrabold tabular-nums leading-none tracking-[-0.02em]"
                style={{ fontSize: '34px', color: SEV_RANK[sev], minWidth: '38px' }}
              >
                {String(idx + 1).padStart(2, '0')}
              </span>

              <div className="min-w-0 flex-1">
                <div
                  className="text-[15px] font-semibold leading-snug"
                  style={{ color: 'rgb(var(--color-text-primary,33 41 56))' }}
                >
                  {rec.title}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
                  <span>{rec.controlCategory}</span>
                  {rec.service && <span>· {rec.service}</span>}
                </div>
              </div>

              <div className="flex flex-none items-center gap-2 pt-1.5">
                <Pill tone={costTone(rec.implementationCost)}>
                  Cost · {rec.implementationCost ?? 'Moderate'}
                </Pill>
                <Pill tone={costTone(rec.userImpact)}>
                  Impact · {rec.userImpact ?? 'Moderate'}
                </Pill>
                <span className="font-display ml-1 inline-flex items-baseline tabular-nums">
                  <span className="text-[16px] font-extrabold italic text-[color:var(--color-navy,#212938)]">
                    +{whole}
                  </span>
                  {frac && (
                    <span className="text-[11px] font-extrabold italic text-[color:var(--color-faint,#9CA3AF)]">
                      {frac}
                    </span>
                  )}
                </span>
                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--color-faint,#9CA3AF)]">
                  pts
                </span>
                <span
                  aria-hidden
                  className="ml-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)] opacity-0 transition-opacity group-hover:opacity-100"
                >
                  ↗ Defender
                </span>
              </div>
            </a>
          </li>
        )
      })}
    </ol>
  )
}

function costTone(level: 'Low' | 'Moderate' | 'High' | undefined): 'low' | 'mod' | 'hi' {
  if (level === 'Low') return 'low'
  if (level === 'High') return 'hi'
  return 'mod'
}

function splitPoints(gap: number): { whole: string; frac: string | null } {
  const rounded = Math.round(gap * 10) / 10
  const whole = Math.trunc(rounded)
  const fracTenths = Math.round((rounded - whole) * 10)
  if (fracTenths === 0) return { whole: String(whole), frac: null }
  return { whole: String(whole), frac: `.${Math.abs(fracTenths)}` }
}

// Defender accepts the controlName (rec.id) as the actionId query param when no
// explicit actionUrl/implementationUrl is published on the profile.
function defenderLinkFor(rec: SecureScoreControlProfile): string {
  return (
    rec.actionUrl
    ?? rec.implementationUrl
    ?? `https://security.microsoft.com/securescore?viewid=actions&actionId=${encodeURIComponent(rec.id)}`
  )
}

function Pill({
  tone,
  children,
}: {
  tone: 'low' | 'mod' | 'hi'
  children: React.ReactNode
}) {
  const styles = {
    low: 'bg-[rgba(118,214,157,0.18)] text-[#3F8259] border-[rgba(74,155,110,0.4)]',
    mod: 'bg-[rgba(167,211,240,0.18)] text-[#3D7393] border-[rgba(79,134,168,0.4)]',
    hi: 'bg-[rgba(235,212,180,0.34)] text-[#A88556] border-[rgba(201,173,128,0.4)]',
  }[tone]

  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-2 py-0.5',
        'text-[10px] font-bold uppercase tracking-[0.12em] tabular-nums',
        styles,
      ].join(' ')}
    >
      {children}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Impact-vs-cost scatter matrix (§7.3)                                       */
/* -------------------------------------------------------------------------- */

/** Editorial fill colors for matrix dots. Low impact = horizon-blue, moderate
 *  = warm-sand, high = severity warm (mirrors the threat-list 'crit' bar). */
const TONE_FILL: Record<MatrixTone, string> = {
  l: '#A7D3F0',
  m: '#C9AD80',
  h: '#C84B4B',
}

const TONE_LABEL: Record<MatrixTone, string> = {
  l: 'Low',
  m: 'Moderate',
  h: 'High',
}

const COST_BUCKETS: readonly CostImpactBucket[] = ['Low', 'Moderate', 'High']
const LEGEND_TONES: readonly MatrixTone[] = ['l', 'm', 'h']

// SVG viewBox geometry. 16:9 plot, padded for axis labels.
const W = 800
const H = 360
const PAD = { top: 24, right: 24, bottom: 44, left: 56 } as const
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

function ImpactCostMatrix({ recs }: { recs: SecureScoreControlProfile[] }) {
  const plottable = recs.filter(isMatrixClassified)
  const unclassifiedCount = recs.length - plottable.length

  if (plottable.length === 0) {
    return (
      <div className="mt-6 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-5">
        <p className="text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
          Microsoft hasn't tagged the cost or impact for any of these recs, so
          there's nothing to plot. They're still listed above.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-6 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-5">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label="Recommendations plotted by implementation cost (x) and points to gain (y), colored by user impact."
      >
        {/* Plot background */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={PLOT_W}
          height={PLOT_H}
          fill="#FBF9F2"
          stroke="#E6E1D5"
        />

        {/* Quadrant divider lines */}
        <line
          x1={PAD.left + PLOT_W / 2}
          x2={PAD.left + PLOT_W / 2}
          y1={PAD.top}
          y2={PAD.top + PLOT_H}
          stroke="#E6E1D5"
          strokeDasharray="3 3"
        />
        <line
          x1={PAD.left}
          x2={PAD.left + PLOT_W}
          y1={PAD.top + PLOT_H / 2}
          y2={PAD.top + PLOT_H / 2}
          stroke="#E6E1D5"
          strokeDasharray="3 3"
        />

        {/* Quadrant labels */}
        <text
          x={PAD.left + 14}
          y={PAD.top + 22}
          fontSize="12"
          fontStyle="italic"
          fontWeight="700"
          fill="#212938"
          letterSpacing="0.04em"
        >
          ★ Quick wins
        </text>
        <text
          x={PAD.left + PLOT_W - 14}
          y={PAD.top + 22}
          fontSize="12"
          fontStyle="italic"
          fontWeight="700"
          fill="#212938"
          letterSpacing="0.04em"
          textAnchor="end"
        >
          Big strategic moves
        </text>
        <text
          x={PAD.left + 14}
          y={PAD.top + PLOT_H - 12}
          fontSize="11"
          fontStyle="italic"
          fill="#6B7280"
          letterSpacing="0.04em"
        >
          Easy maintenance
        </text>
        <text
          x={PAD.left + PLOT_W - 14}
          y={PAD.top + PLOT_H - 12}
          fontSize="11"
          fontStyle="italic"
          fill="#6B7280"
          letterSpacing="0.04em"
          textAnchor="end"
        >
          Heavy lift, low payoff
        </text>

        {/* Dots */}
        {plottable.map((rec) => {
          const p = placeOnMatrix(rec)
          const cost = rec.implementationCost ?? 'Moderate'
          const impact = TONE_LABEL[p.tone]
          const { whole, frac } = splitPoints(p.gap)
          const ptsLabel = frac ? `${whole}${frac}` : whole
          const tooltip = `${rec.title} · Cost: ${cost} · Impact: ${impact} · ${ptsLabel} pts · Click to open in Defender`
          return (
            <a
              key={rec.id}
              href={defenderLinkFor(rec)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={PAD.left + p.x * PLOT_W}
                cy={PAD.top + p.y * PLOT_H}
                r={6.5}
                fill={TONE_FILL[p.tone]}
                fillOpacity={0.9}
                stroke="#212938"
                strokeOpacity={0.25}
                strokeWidth={1}
              >
                <title>{tooltip}</title>
              </circle>
            </a>
          )
        })}

        {/* X-axis label */}
        <text
          x={PAD.left + PLOT_W / 2}
          y={H - 10}
          fontSize="11"
          fill="#6B7280"
          textAnchor="middle"
          letterSpacing="0.16em"
          fontWeight="700"
          style={{ textTransform: 'uppercase' }}
        >
          Implementation cost →
        </text>

        {/* Y-axis label (rotated) */}
        <text
          x={-PAD.top - PLOT_H / 2}
          y={16}
          transform="rotate(-90)"
          fontSize="11"
          fill="#6B7280"
          textAnchor="middle"
          letterSpacing="0.16em"
          fontWeight="700"
          style={{ textTransform: 'uppercase' }}
        >
          Points to gain ↑
        </text>

        {/* X-axis tick markers */}
        {COST_BUCKETS.map((label) => (
          <text
            key={label}
            x={PAD.left + X_BY_COST[label] * PLOT_W}
            y={PAD.top + PLOT_H + 18}
            fontSize="10"
            fill="#9CA3AF"
            textAnchor="middle"
            letterSpacing="0.12em"
            style={{ textTransform: 'uppercase' }}
          >
            {label}
          </text>
        ))}
      </svg>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
        <span className="font-bold">Impact</span>
        {LEGEND_TONES.map((tone) => (
          <span key={tone} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: TONE_FILL[tone] }}
            />
            {TONE_LABEL[tone]}
          </span>
        ))}
      </div>

      {plottable.length > 0 && (
        <p className="mt-2 text-[11px] italic text-[color:var(--color-faint,#9CA3AF)]">
          Each dot links out to the matching action in Microsoft Defender.
        </p>
      )}

      {unclassifiedCount > 0 && (
        <p className="mt-3 text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
          {unclassifiedCount === 1
            ? '1 rec isn’t plotted — Microsoft hasn’t classified its cost or impact.'
            : `${unclassifiedCount} recs aren’t plotted — Microsoft hasn’t classified their cost or impact.`}
        </p>
      )}
    </div>
  )
}

function ThreatList({
  threats,
  recs,
}: {
  threats: ReturnType<typeof aggregateThreats>
  recs: SecureScoreControlProfile[]
}) {
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <ul className="mt-6 overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]">
      {threats.map((t) => {
        const sev = severityFromCoverage(t.coverage)
        const isOpen = expanded === t.threat
        const panelId = `threat-panel-${t.threat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
        const matching = recs
          .filter((r) => (r.threats ?? []).includes(t.threat))
          .sort((a, b) => {
            // Open recs (biggest gap first) before implemented.
            if (a.isImplemented !== b.isImplemented) return a.isImplemented ? 1 : -1
            const gA = Math.max(0, (a.maxScore ?? 0) - (a.achievedScore ?? 0))
            const gB = Math.max(0, (b.maxScore ?? 0) - (b.achievedScore ?? 0))
            if (gA !== gB) return gB - gA
            return (a.title ?? '').localeCompare(b.title ?? '')
          })

        return (
          <li
            key={t.threat}
            className="border-b border-[color:var(--color-rule,#E6E1D5)] last:border-b-0"
          >
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : t.threat)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="grid w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[color:var(--color-cream,#FBF8F2)]/40"
              style={{ gridTemplateColumns: '4px 1fr 80px 14px' }}
            >
              <span
                aria-hidden
                className="self-stretch rounded-full"
                style={{ background: SEV_BAR[sev] }}
              />
              <div>
                <div className="text-[14px] font-semibold text-[color:var(--color-navy,#212938)]">
                  {t.threat}
                </div>
                <div className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
                  {t.implemented} of {t.total} controls implemented
                </div>
              </div>
              <span
                className="font-display text-right text-[16px] font-extrabold italic tabular-nums"
                style={{ color: SEV_BAR[sev] }}
              >
                {Math.round(t.coverage * 100)}%
              </span>
              <ChevronDownIcon
                aria-hidden
                className="h-3.5 w-3.5 text-[color:var(--color-mute,#6B7280)] transition-transform"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>

            {isOpen && (
              <div
                id={panelId}
                className="border-t border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]/30 px-5 py-3"
              >
                {matching.length === 0 ? (
                  <p className="px-2 py-1.5 text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
                    No matching controls in the current dataset.
                  </p>
                ) : (
                  <ol className="space-y-0.5">
                    {matching.map((rec) => {
                      const gap = Math.max(0, (rec.maxScore ?? 0) - (rec.achievedScore ?? 0))
                      const { whole, frac } = splitPoints(gap)
                      return (
                        <li key={rec.id}>
                          <a
                            href={defenderLinkFor(rec)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-3 rounded-[2px] px-2 py-1.5 no-underline transition-colors hover:bg-[color:var(--color-paper,#fff)]"
                          >
                            <span
                              aria-hidden
                              className={[
                                'inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border text-[10px] font-bold',
                                rec.isImplemented
                                  ? 'border-[#4A9B6E] bg-[rgba(118,214,157,0.18)] text-[#3F8259]'
                                  : 'border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-faint,#9CA3AF)]',
                              ].join(' ')}
                              title={rec.isImplemented ? 'Implemented' : 'Open'}
                            >
                              {rec.isImplemented ? '✓' : ''}
                            </span>
                            <span
                              className={[
                                'min-w-0 flex-1 truncate text-[13px]',
                                rec.isImplemented
                                  ? 'italic text-[color:var(--color-mute,#6B7280)]'
                                  : 'font-medium text-[color:var(--color-navy,#212938)]',
                              ].join(' ')}
                            >
                              {rec.title}
                            </span>
                            {rec.service && (
                              <span className="hidden flex-none text-[10px] uppercase tracking-[0.12em] text-[color:var(--color-faint,#9CA3AF)] md:inline">
                                {rec.service}
                              </span>
                            )}
                            {!rec.isImplemented && gap > 0 && (
                              <span className="font-display flex-none inline-flex items-baseline tabular-nums">
                                <span className="text-[13px] font-extrabold italic text-[color:var(--color-navy,#212938)]">
                                  +{whole}
                                </span>
                                {frac && (
                                  <span className="text-[10px] font-extrabold italic text-[color:var(--color-faint,#9CA3AF)]">
                                    {frac}
                                  </span>
                                )}
                                <span className="ml-1 text-[9px] uppercase tracking-[0.12em] text-[color:var(--color-faint,#9CA3AF)]">
                                  pts
                                </span>
                              </span>
                            )}
                            <span
                              aria-hidden
                              className="flex-none text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)] opacity-0 transition-opacity group-hover:opacity-100"
                            >
                              ↗ Defender
                            </span>
                          </a>
                        </li>
                      )
                    })}
                  </ol>
                )}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function ServiceCoverage({
  rows,
}: {
  rows: ReturnType<typeof coverageByService>
}) {
  return (
    <ul className="mt-6 space-y-3">
      {rows.map((row) => {
        const tone =
          row.pct >= 80 ? 'healthy' : row.pct >= 50 ? 'mid' : 'warn'
        const fill =
          tone === 'healthy' ? '#4A9B6E' : tone === 'mid' ? '#4F86A8' : '#C9AD80'
        return (
          <li
            key={row.service}
            className="grid items-center gap-3"
            style={{ gridTemplateColumns: '120px 1fr 70px' }}
          >
            <span className="text-[12px] font-semibold text-[color:var(--color-navy,#212938)]">
              {row.service}
            </span>
            <span
              aria-hidden
              className="block h-3 overflow-hidden rounded-full bg-[#F0EBDD]"
            >
              <span
                className="block h-full rounded-full"
                style={{ width: `${row.pct}%`, background: fill }}
              />
            </span>
            <span
              className="text-right font-mono text-[12px] tabular-nums text-[color:var(--color-navy,#212938)]"
            >
              {row.pct}% · {row.implemented}/{row.total}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function SecureScoreLoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6" data-tone="bpa">
      <div className="h-40 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="h-72 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]" />
        </div>
        <div className="lg:col-span-2">
          <div className="h-72 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]" />
        </div>
      </div>
      <div className="h-96 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]" />
    </div>
  )
}
